import { DateTime, Info } from 'luxon';

import {
  AlocacoesQuery_alocacoes_professores,
  AlocacoesQuery_alocacoes_professores_disciplina,
  AlocacoesQuery_alocacoes_professores_professor,
} from '../../graphql/queries/__generated__/AlocacoesQuery';

import { FeriadosQuery_feriados } from '../../graphql/queries/__generated__/FeriadosQuery';
import { ICorProfessor } from '../../reducers/alocacao.reducer';
import { mapear_alocacoes } from '../maps/alocacoes.map';
import { mapear_cores_professores } from '../maps/cores_professores.map';
import { mapear_feriados } from '../maps/feriados.map';

interface IAlocacao {
  id_alocacao?: number;
  id_cor?: number;
  dia_numero: number;
  dia_semana: string;
  dat_alocacao: string;
  dsc_feriado?: string | null;
  disciplina?: AlocacoesQuery_alocacoes_professores_disciplina;
  professor?: AlocacoesQuery_alocacoes_professores_professor;
  fim_semana: boolean;
  planejamento?: boolean;
}

const meses = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface Props {
  dat_inicio: string;
  dat_fim: string;
  alocacoes: AlocacoesQuery_alocacoes_professores[];
  feriados: FeriadosQuery_feriados[];
  ocultar_dias: string[];
  cores_professores: ICorProfessor[];
}

/**
 * Gera grade de alocação que será exibida na tela
 * */
const gerar_grades_alocacoes = async (props: Props): Promise<IAlocacao[][]> => {
  let data_inicial = DateTime.fromISO(props.dat_inicio, { setZone: true });
  const data_final = DateTime.fromISO(props.dat_fim, { setZone: true });
  const calendario: IAlocacao[][] = [];
  const map_alocacoes = mapear_alocacoes(props.alocacoes);
  const map_feriados = mapear_feriados(props.feriados);
  const map_cores = mapear_cores_professores(props.cores_professores);

  while (data_inicial.startOf('day') <= data_final.startOf('day')) {
    const mes = data_inicial.month - 1;
    const dia = data_inicial.day;

    const dia_semana = formatar_dia_semana(
      Info.weekdays('short', { locale: 'pt-BR' })[data_inicial.weekday - 1]
    );

    if (!props.ocultar_dias.includes(dia_semana)) {
      if (!Array.isArray(calendario[mes])) {
        calendario[mes] = [];
      }

      const chaveMap = data_inicial.startOf('day').toISODate();
      const feriado = map_feriados.get(chaveMap);
      const alocacao = map_alocacoes.get(chaveMap);

      if (feriado && props.ocultar_dias.includes('FERIADO')) {
        data_inicial = data_inicial.plus({ days: 1 });
        continue;
      }

      if (alocacao && props.ocultar_dias.includes('ALOCADO')) {
        data_inicial = data_inicial.plus({ days: 1 });
        continue;
      }

      const id_cor = map_cores.get(alocacao?.professor?.id_professor || 0);

      calendario[mes].push({
        id_alocacao: alocacao?.id_alocacao,
        dia_numero: dia,
        dia_semana,
        dat_alocacao: data_inicial.toISO(),
        id_cor: id_cor || 1,
        dsc_feriado: feriado?.dsc_feriado,
        professor: alocacao?.professor,
        disciplina: alocacao?.disciplina,
        fim_semana: data_inicial.weekday === 6 || data_inicial.weekday === 7,
        planejamento: alocacao?.planejamento,
      });
    }

    data_inicial = data_inicial.plus({ days: 1 });
  }

  return calendario;
};

const formatar_dia_semana = (diaSemana: string): string => {
  return diaSemana.toUpperCase().slice(0, -1);
};

export { gerar_grades_alocacoes, meses };
export type { IAlocacao };
